<template>
  <div>
    <div class="b-calender__wrapper">
      <div
        :class="[
          miniCalendar
            ? 'b-calender__calender-holder--mini'
            : 'b-calender__calender-holder',
        ]">
        <Swiper
          ref="CalenderSwiper"
          :navigation="{
            nextEl: '.b-calender__next',
            prevEl: '.b-calender__prev',
          }"
          :pagination="{
            el: '.b-calender__pagination',
            type: 'bullets',
            clickable: true,
          }"
          :slides-per-view="daysPerView"
          :space-between="5"
          :breakpoints="breakpoints"
          @swiper="onSwiper">
          <SwiperSlide
            v-for="(cal, index) in calendar"
            :id="cal.fullDate"
            :key="index">
            <div
              :class="[calendarContainerClass, allowedDatesClass(cal)]"
              @click="chooseDate(cal)">
              <div
                v-if="!miniCalendar"
                class="b-calender__day"
                :class="[
                  isCurrentActive(cal),
                  isCurrentToday(cal),
                  isCurrentDisabled(cal),
                ]">
                <div v-if="cal.currentDay" class="b-calender--today">
                  {{ getTodayLocalString(local) }}
                </div>
                <div class="b-calender__title">{{ cal.dayName }}</div>
                <div class="b-calender__number">{{ cal.day }}</div>
                <div class="b-calender__title">
                  {{ cal.monthName }}
                </div>
              </div>
              <div
                v-else
                class="e-calender__day"
                :class="{
                  'e-calender--current': cal.currentDay,
                  'e-calender--active': isCurrentActive(cal),
                }">
                <div class="e-calender__title">{{ cal.initialDayName }}</div>
                <div>{{ cal.day }}</div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div v-if="!miniCalendar && desktop" class="b-calender__slider-actions">
        <div
          class="swiper-button-next b-calender__next"
          @click="() => slider.slideNext()"></div>
        <div
          class="swiper-button-prev b-calender__prev"
          @click="() => slider.slidePrev()"></div>
      </div>
    </div>
    <div v-if="miniCalendar && !hideNavigation" class="p-movies__cta-holder">
      <button
        type="button"
        class="swiper-button-prev p-movies__prev-cta b-calender__prev"
        @click="() => slider.slidePrev()"></button>
      <button
        type="button"
        class="swiper-button-next p-movies__next-cta b-calender__next"
        @click="() => slider.slideNext()"></button>
    </div>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { removeDiacritics } from '../../lib/lib'
import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue'
import {
  getDateDay,
  getDayName,
  getDayShortName,
  getFormattedDate,
  getMonthShortName,
  getTodayLocalString,
} from '../../lib/date'
import useViewport from '../../features/viewport'
import moment from 'moment'
import 'moment-timezone'
import constants from './../../lib/constants'

const { desktop } = useViewport()

defineComponent({
  Swiper,
  SwiperSlide,
})

const props = defineProps({
  modelValue: {
    default() {
      return ''
    },
    type: String,
  },
  dateFormateOptions: {
    default() {
      return {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }
    },
    type: Object,
  },
  local: {
    default: 'el',
    type: String,
  },
  daysPerView: {
    default: 7,
    type: Number,
  },
  miniCalendar: {
    default: false,
    type: Boolean,
  },
  hideNavigation: {
    default: false,
    type: Boolean,
  },
  breakpoints: {
    default() {
      return {}
    },
    type: Object,
  },
  allowedDates: {
    default() {
      return []
    },
    type: Array,
  },
  hideDisabledDates: {
    default: false,
    type: Boolean,
  },
})

const emit = defineEmits(['choose-date', 'update:modelValue', 'days-data'])

const slider = ref(null)
const calendar = ref([])

watch(
  () => props.allowedDates,
  () => {
    getCalendarByNumberOfDays(maxDaysToShow.value)
    nextTick(() => {
      slider.value.slideTo(minAllowedDateIndex.value, 1000)
    })
  }
)
const calendarContainerClass = computed(() => {
  return props.miniCalendar ? 'e-calender__el-holder' : 'b-calender__el-holder'
})

const minAllowedDate = computed(() => {
  return moment.min(
    props.allowedDates.map((x) => moment.tz(x, constants.timezone))
  )
})

const maxAllowedDate = computed(() => {
  return moment.max(
    props.allowedDates.map((x) => moment.tz(x, constants.timezone))
  )
})

const maxDaysToShow = computed(() => {
  const { defaultDays } = constants.calendar
  // today & screen day as offset
  const offsetDays = 2
  const today = moment().tz(constants.timezone)
  const diff = maxAllowedDate.value.diff(today, 'days') + offsetDays
  return diff > defaultDays ? diff : defaultDays
})

const minAllowedDateIndex = computed(() => {
  const value = minAllowedDate.value.format('YYYY-MM-DD')
  const index = calendar.value.findIndex((x) => x.dateValue === value)

  return index > 0 ? index : 0
})

const onSwiper = (swiper) => {
  slider.value = swiper
}

onMounted(async () => {
  getCalendarByNumberOfDays(maxDaysToShow.value)
  await nextTick()
  slider.value.slideTo(minAllowedDateIndex.value)
  emit('days-data', calendar)
})

const getCalendarByNumberOfDays = (numberOfDays) => {
  const calendarData = []

  let index = 0
  while (index < numberOfDays) {
    const today = moment().tz(constants.timezone)
    const currentDate = today.add(index, 'days')

    const isTodayDate =
      index === 0
        ? getDateDay(today.toDate())
        : getDateDay(currentDate.toDate())

    calendarData.push({
      day: isTodayDate,
      dayName: removeDiacritics(
        getDayShortName(currentDate.toDate(), props.local).toUpperCase()
      ),
      initialDayName: getDayName(currentDate.toDate(), props.local).slice(0, 1),
      monthName: removeDiacritics(
        getMonthShortName(currentDate.toDate(), props.local).toUpperCase()
      ),
      currentDay: index === 0,
      fullDate: currentDate.toDate(),
      formattedDate: getFormattedDate(
        currentDate.toDate(),
        props.local,
        props.dateFormateOptions
      ),
      dateValue: currentDate.toISOString(true).split('T')[0],
    })
    index += 1
  }
  calendar.value = [...calendarData]
}

const isCurrentActive = (currentDate) => {
  if (props.miniCalendar) {
    return currentDate.dateValue === props.modelValue
      ? 'e-calender--active'
      : ''
  }
  return currentDate.dateValue === props.modelValue ? 'b-calender--active' : ''
}

const chooseDate = (date) => {
  if (isDateAllowed(date)) {
    emit('choose-date', date)
    emit('update:modelValue', date.dateValue)
  }
}

const isCurrentToday = (currentDate) => {
  return currentDate.currentDay && 'e-calender--current-today'
}

const isDateAllowed = (date) => {
  // date should be a calendar object here
  return props.allowedDates.includes(date.dateValue)
}

const allowedDatesClass = (date) => {
  if (isDateAllowed(date)) {
    return ''
  }
  return 'b-calender__box--disabled'
}
const isCurrentDisabled = (date) => {
  if (!isDateAllowed(date)) {
    return 'b-calender__day--disabled'
  }
  return ''
}
</script>
